import styled from '@emotion/styled'
import { zIndex } from '../../__styling/settings/z-index'
import { calcRem } from '../../__styling/style-functions/calc-rem'
import { breakpoint } from '../../__styling'
import { colorsPN } from '@news-mono/design-tokens'

export const StyledGridItem = styled('li')<{
    cardSpacing: number
    noHorizontalDividers?: boolean
    hideLastDivider?: boolean
    hideTopDivider?: boolean
    horizontalDividerColor?: string
    topBottomDividerColorOverride?: string
}>(
    {
        display: 'flex',
        justifyContent: 'flex-start',
        flexGrow: 1,
        flexShrink: 1,
        width: '100%',
        maxWidth: '100%',
        alignItems: 'stretch',
        flex: '0 0 auto',
        position: 'relative',
    },
    ({
        horizontalDividerColor,
        cardSpacing,
        hideLastDivider = true,
        hideTopDivider = true,
        topBottomDividerColorOverride,
    }) => ({
        paddingTop: calcRem(cardSpacing),
        paddingBottom: calcRem(cardSpacing),

        '&:first-child': {
            marginTop: '0',
            paddingTop: hideTopDivider ? 0 : calcRem(cardSpacing),
        },

        '&:last-child': {
            marginBottom: '0',
            paddingBottom: hideLastDivider ? 0 : calcRem(cardSpacing),
        },

        // Adds horizontal dividers
        '&:first-child::before': hideTopDivider
            ? undefined
            : {
                  content: `''`,
                  backgroundColor:
                      topBottomDividerColorOverride ||
                      horizontalDividerColor ||
                      colorsPN.stroke.brand.strong,
                  width: '100%',
                  height: calcRem(1),
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  zIndex: zIndex.common.collectionDividers,
              },
        '&::after': {
            content: `''`,
            backgroundColor:
                horizontalDividerColor || colorsPN.stroke.brand.strong,
            width: '100%',
            height: calcRem(1),
            position: 'absolute',
            left: 0,
            bottom: 0,
            zIndex: zIndex.common.collectionDividers,
        },

        '&:last-child::after': hideLastDivider
            ? {
                  content: undefined,
                  height: 0,
              }
            : {
                  backgroundColor:
                      topBottomDividerColorOverride ||
                      horizontalDividerColor ||
                      colorsPN.stroke.brand.strong,
              },

        '> .PortraitCard': {
            alignItems: 'flex-start',
        },
    }),
)

export interface DisplayOnBreakpoint {
    breakpoint: 'xxs' | 'xs' | 'sm' | 'md' | 'lg'
    inverse?: boolean
}

// To decide whether to display the element based on breakpoint
function getDisplayStyle(
    displayType: 'grid' | 'flex',
    displayOnBreakpoint?: DisplayOnBreakpoint,
) {
    // always display by default
    if (!displayOnBreakpoint) {
        return {
            display: displayType,
        }
    }

    return {
        display: displayOnBreakpoint.inverse ? 'none' : displayType,
        [breakpoint(displayOnBreakpoint.breakpoint)]: {
            display: displayOnBreakpoint.inverse ? displayType : 'none',
        },
    }
}

export const StyledRomeoTopDivider = styled('div')<{ dividerColor?: string }>(
    ({ dividerColor }) => ({
        width: '100%',
        height: calcRem(1),
        backgroundColor: dividerColor || colorsPN.stroke.brand.strong,
        marginBottom: calcRem(20),
    }),
)

export const StyledRomeoWrapper = styled('div')<{
    displayOnBreakpoint?: DisplayOnBreakpoint
    centreOnTablet?: boolean
}>(({ displayOnBreakpoint, centreOnTablet }) => ({
    ...getDisplayStyle('grid', displayOnBreakpoint),
    gridTemplateColumns: 'repeat(5, 1fr)',
    gridGap: calcRem(24),
    width: '100%',

    // Hide if there are no items shown.
    [`:not(:has(${StyledGridItem}))`]: {
        display: 'none',
    },

    [`& > ${StyledRomeo}`]: {
        gridColumnStart: 1,
        gridColumnEnd: 6,
        [breakpoint('sm')]: centreOnTablet
            ? {
                  gridColumnStart: 2,
                  gridColumnEnd: 5,
              }
            : undefined,
        [breakpoint('lg')]: {
            gridColumnStart: 1,
            gridColumnEnd: 6,
        },
    },
}))

export interface StyledRomeoProps {
    displayOnBreakpoint?: DisplayOnBreakpoint
    marginTop?: number
    marginBottom?: number
}

export const StyledRomeo = styled('div')<StyledRomeoProps>(
    ({ displayOnBreakpoint }) => ({
        ...getDisplayStyle('flex', displayOnBreakpoint),
        margin: '0 auto',
        width: '100%',
        flexWrap: 'wrap',
    }),
    ({ marginTop, marginBottom = 32 }) => [
        {
            marginTop: calcRem(marginTop || 0),
            marginBottom: calcRem(marginBottom),
        },
    ],
)

export const StyledHeader = styled('div')({
    display: 'block',
    width: '100%',
})

export const StyledGridWrapper = styled('ul')({
    display: 'block',
    width: '100%',
    margin: 0,
    padding: 0,
})
